import _extends from "@babel/runtime/helpers/extends";
import React, { useMemo } from 'react';
import { useCheckboxGroup } from '@react-aria/checkbox';
import { CheckboxGroupContext, useCheckboxGroupState } from './hooks/CheckboxGroupContext';
import { alto, Anchor, mineShaft, thunderBird } from '@parchment/atoms';
import styled, { css } from 'styled-components';
import { ValidationRequiredIcon } from '@parchment/icons';
import { CheckboxGroupCheckbox } from './CheckboxGroupCheckbox';
import { useValidator } from '../FieldValidation/useValidator';
import { mergeProps } from '@react-aria/utils';
function CheckboxGroup(props) {
  const {
    children,
    label,
    canSelectAll = false,
    canSelectNone = false,
    direction
  } = props;
  const state = useCheckboxGroupState(props);
  const {
    groupProps,
    labelProps
  } = useCheckboxGroup(props, state);
  const asterisk = props.isRequired && label ? requiredIcon : null;
  const requiredMsg = props.isRequired && state.registeredValues.length > 1 ? requiredNote : null;
  const selectAll = canSelectAll ? /*#__PURE__*/React.createElement(Selector, {
    key: "select-all",
    name: "select-all",
    onClick: state.selectAll
  }, "Select All") : null;
  const selectNone = canSelectNone ? /*#__PURE__*/React.createElement(Selector, {
    key: "select-none",
    name: "select-none",
    onClick: state.selectNone
  }, "Select None") : null;
  const selectorItems = [selectAll, selectNone].filter(selector => selector).reduce((selectors, selector, idx) => {
    return [...selectors, idx > 0 ? /*#__PURE__*/React.createElement(Separator, {
      key: `separator-${idx}`
    }) : null, selector];
  }, []);
  const selectors = selectorItems.length > 0 ? /*#__PURE__*/React.createElement(Selectors, null, selectorItems) : null;
  const validators = useMemo(() => {
    const validators = props.validators || [];
    if (props.isRequired) {
      validators.push(requiredValidator);
    }
    return validators;
  }, [props.validators, props.isRequired]);
  const {
    validatorProps
  } = useValidator({
    value: state.value,
    onValidate: props.onValidate,
    validators
  });
  if (props.isRequired) {
    groupProps['aria-required'] = true;
  }
  return /*#__PURE__*/React.createElement(StyledGroup, _extends({}, mergeProps(groupProps, validatorProps), {
    className: props.className
  }), /*#__PURE__*/React.createElement(Label, labelProps, asterisk, label), selectors, /*#__PURE__*/React.createElement(CheckboxGroupContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(Items, {
    layoutDirection: direction
  }, children)), requiredMsg);
}
CheckboxGroup.Checkbox = CheckboxGroupCheckbox;
export { CheckboxGroup };
const requiredValidator = {
  callback: value => (value || []).length > 0,
  message: 'At least one option is required.'
};
const Separator = /*#__PURE__*/styled.span.attrs(attrs => ({
  role: 'separator',
  'aria-orientation': 'vertical',
  ...attrs
})).withConfig({
  displayName: "CheckboxGroup__Separator",
  componentId: "sc-1muuydj-0"
})(["border-right:1px solid ", ";margin-left:0.5em;margin-right:0.5em;"], alto);
const StyledGroup = /*#__PURE__*/styled.div.withConfig({
  displayName: "CheckboxGroup__StyledGroup",
  componentId: "sc-1muuydj-1"
})(["color:", ";"], mineShaft);
const Label = /*#__PURE__*/styled.div.withConfig({
  displayName: "CheckboxGroup__Label",
  componentId: "sc-1muuydj-2"
})(["font-weight:normal;margin-bottom:0.5em;"]);
const Selectors = /*#__PURE__*/styled.div.withConfig({
  displayName: "CheckboxGroup__Selectors",
  componentId: "sc-1muuydj-3"
})(["margin-bottom:0.5em;margin-left:-5px;display:flex;button{padding-left:5px;padding-right:5px;}"]);
const itemCssVertical = /*#__PURE__*/css(["flex-direction:column;"]);
const itemCssHorizontal = /*#__PURE__*/css(["flex-direction:row;"]);
const Items = /*#__PURE__*/styled.div.withConfig({
  displayName: "CheckboxGroup__Items",
  componentId: "sc-1muuydj-4"
})(["display:flex;flex-wrap:wrap;", " margin-left:-10px;> *{margin-left:10px;margin-right:10px;}"], _ref => {
  let {
    layoutDirection
  } = _ref;
  return layoutDirection === 'horizontal' ? itemCssHorizontal : itemCssVertical;
});
const Selector = /*#__PURE__*/styled(Anchor).attrs(attrs => ({
  as: 'button',
  ...attrs
})).withConfig({
  displayName: "CheckboxGroup__Selector",
  componentId: "sc-1muuydj-5"
})(["font-size:13px;padding:0;"]);
const RequiredIcon = /*#__PURE__*/styled.span.withConfig({
  displayName: "CheckboxGroup__RequiredIcon",
  componentId: "sc-1muuydj-6"
})(["color:", " margin-right:0.5em;"], thunderBird);
const RequiredNote = /*#__PURE__*/styled.div.withConfig({
  displayName: "CheckboxGroup__RequiredNote",
  componentId: "sc-1muuydj-7"
})(["color:", " font-size:13px;font-style:italic;"], thunderBird);
const requiredIcon = /*#__PURE__*/React.createElement(RequiredIcon, null, /*#__PURE__*/React.createElement(ValidationRequiredIcon, null));
const requiredNote = /*#__PURE__*/React.createElement(RequiredNote, null, "at least one option is required");